<template>
  <div class="announcements-page">
    <div class="announcements-inner">
      <div class="announcements-page-header">
        <a role="button" tabindex="0" aria-label="Go back" @click="$router.go(-1)" @keydown.enter.prevent="$router.go(-1)" @keydown.space.prevent="$router.go(-1)">
          <i class="fas fa-arrow-left" aria-hidden="true"></i>
        </a>
        <div class="title-wrap">
          <span class="page-label">Announcements</span>
          <span v-if="!isFilterCountLoaded" class="title-count-shimmer"></span>
          <span v-else-if="unreadForFilter > 0" class="title-count" :title="unreadForFilter + ' unread'">{{ unreadForFilter }}</span>
        </div>
      </div>

      <div class="header-actions">
        <div class="filter-row" :class="{ 'filter-row-search-open': searchOpen }">
          <button v-if="!searchOpen" type="button" class="icon-btn" title="Search announcements"
            aria-label="Search announcements" @click="openSearch">
            <i class="fas fa-search"></i>
          </button>
          <div v-else class="search-input-wrap">
            <i class="fas fa-search search-icon" aria-hidden="true"></i>
            <input ref="searchInput" type="search" class="search-input" placeholder="Search by id or title… (press Enter)"
              v-model="searchInput" @keydown.enter.prevent="commitSearch" @keydown.esc="closeSearch" @blur="onSearchBlur" />
            <button type="button" class="icon-btn search-close" title="Close search" aria-label="Close search"
              @click="closeSearch">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="filter-controls" v-show="!searchOpen">
            <template v-if="!isCourseMode">
              <button class="scope-filter-btn flex" :class="{ active: scope === 'all' }" @click="setScope('all')">All</button>
              <button class="scope-filter-btn flex" :class="{ active: scope === 'global' }" @click="setScope('global')">Global</button>
              <div class="course-filter-wrapper">
                <i class="fas fa-graduation-cap course-filter-icon" aria-hidden="true"></i>
                <v-select class="course-filter" :options="enrolledCourses" :reduce="course => course.id" v-model="selectedCourseId"
                  label="title" placeholder="Select course" :filter-by="courseFilterBy" @input="onCoursePicked">
                  <template #option="{ id, title }">{{ id }} - {{ title }}</template>
                </v-select>
              </div>
            </template>

            <button class="scope-filter-btn" :class="{ active: onlyUnread, flex: !isCourseMode }" @click="onlyUnread = !onlyUnread">Unread</button>
          </div>
        </div>
      </div>

      <div v-if="!searchOpen && searchQuery" class="search-active">
        <span>Filter: <strong>{{ searchQuery }}</strong></span>
        <button type="button" class="link-btn" @click="clearSearch">Clear</button>
      </div>

      <AnnouncementList
        :entity-type="entityType"
        :entity-id="entityId"
        :only-global="onlyGlobal"
        :only-unread="onlyUnread"
        :search-query="searchQuery" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import AnnouncementList from '../components/Announcement/AnnouncementList.vue';
import { AnnouncementScope, AnnouncementRouteScope } from '../constants/announcement';
import { ProductVariantStatus, ProductVariantSubscriptionStatus } from '../constants/productVariant';

export default {
  name: 'Announcements',
  components: { AnnouncementList, vSelect },
  data() {
    return {
      onlyUnread: false,
      scope: 'all',
      selectedCourseId: null,
      searchOpen: false,
      searchInput: '',
      searchQuery: '',
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('announcement', ['unreadCount', 'isCountLoaded']),

    unreadForFilter() {
      return this.unreadCount({ entityType: this.entityType, entityId: this.entityId, onlyGlobal: this.onlyGlobal, searchQuery: this.searchQuery || null });
    },
    isFilterCountLoaded() {
      return this.isCountLoaded({ entityType: this.entityType, entityId: this.entityId, onlyGlobal: this.onlyGlobal, searchQuery: this.searchQuery || null });
    },

    isCourseMode() {
      return this.equalsIgnoreCase(this.$route.params.scope, AnnouncementRouteScope.Course) && !!this.$route.params.entityId;
    },
    enrolledCourses() {
      const now = new Date();
      return ((this.user && this.user.productVariants) || [])
        .filter(productVariant => {
          const isPublished = !this.equalsIgnoreCase(productVariant.status, ProductVariantStatus.DRAFT);
          const isAllowed = this.equalsIgnoreCase(productVariant.subscriptionStatus, ProductVariantSubscriptionStatus.ALLOWED);
          const isWithinBatchWindow = !productVariant.endDate || new Date(productVariant.endDate) >= now;
          const isWithinAccessWindow = !productVariant.expiresAt || new Date(productVariant.expiresAt) >= now;
          return isPublished && productVariant.product && productVariant.product.title && isAllowed && isWithinBatchWindow && isWithinAccessWindow;
        })
        .map(productVariant => ({ id: productVariant.id, title: productVariant.product.title + (productVariant.title ? ' - ' + productVariant.title : '') }));
    },
    entityType() {
      if (this.isCourseMode) return AnnouncementScope.ProductVariant;
      if (this.scope === 'course' && this.selectedCourseId) return AnnouncementScope.ProductVariant;
      return null;
    },
    entityId() {
      if (this.isCourseMode) return this.$route.params.entityId;
      if (this.scope === 'course') return this.selectedCourseId;
      return null;
    },
    onlyGlobal() {
      if (this.isCourseMode) return false;
      return this.scope === 'global';
    },
  },
  methods: {
    equalsIgnoreCase(a, b) {
      if (a == null || b == null) return a === b;
      return String(a).toLowerCase() === String(b).toLowerCase();
    },
    openSearch() {
      this.searchOpen = true;
      this.searchInput = this.searchQuery;
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    closeSearch() {
      this.searchOpen = false;
    },
    onSearchBlur() {
      if (!this.searchInput) this.closeSearch();
    },
    commitSearch() {
      this.searchQuery = this.searchInput.trim();
    },
    clearSearch() {
      this.searchInput = '';
      this.searchQuery = '';
    },
    courseFilterBy(option, label, search) {
      const term = (search || '').toLowerCase();
      return String(option.id).toLowerCase().includes(term) || String(option.title || '').toLowerCase().includes(term);
    },
    setScope(scope) {
      this.scope = scope;
      this.selectedCourseId = null;
    },
    onCoursePicked(courseId) {
      this.scope = courseId ? 'course' : 'all';
    },
  },
  created() {
    if (!this.isCourseMode && this.equalsIgnoreCase(this.$route.params.scope, AnnouncementRouteScope.Global)) {
      this.scope = 'global';
    }
  },
};
</script>

<style scoped>
.announcements-page {
  min-height: calc(100vh - var(--header-height));
  background: #f5f5f5;
  padding-bottom: 2rem;
}

.announcements-inner {
  max-width: 960px;
  margin: 0 auto;
  padding: 1rem 1.5rem 0;
}

.announcements-page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 2px 14px;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.announcements-page-header .page-label {
  font-size: 1.5rem;
  font-weight: 500;
}

.title-count {
  background: red;
  color: #fff;
  border-radius: 999px;
  min-width: 22px;
  padding: 2px 8px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}

.title-count-shimmer {
  position: relative;
  display: inline-block;
  width: 26px;
  height: 22px;
  border-radius: 999px;
  background: #e2e2e2;
  overflow: hidden;
}

.title-count-shimmer::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.65) 50%, rgba(255, 255, 255, 0) 100%);
  transform: translateX(-100%);
  animation: title-count-shimmer 1.6s infinite linear;
}

@keyframes title-count-shimmer {
  100% { transform: translateX(100%); }
}

.announcements-page-header i {
  font-size: large;
}

.header-actions {
  background: #fff;
  padding: .4rem 0.8rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.filter-row-search-open {
  flex-wrap: nowrap;
}

.icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #ddd;
  color: #606060;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.icon-btn:hover {
  background-color: #f2f2f2;
  color: #0f0f0f;
}

.search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #909090;
  font-size: 12px;
  pointer-events: none;
}

.search-input {
  flex: 1;
  width: 100%;
  padding: 8px 38px 8px 34px;
  border: 1px solid #ececec;
  border-radius: 999px;
  background: #fafafa;
  font-size: 0.85rem;
  color: #0f0f0f;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.search-input:focus {
  background: #fff;
  border-color: #d0d0d0;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.04);
}

.search-close {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  /* Sits inside the search field, so override the icon-btn border/background. */
  border: none;
  background: transparent;
}

.scope-filter-btn {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.78rem;
  letter-spacing: 0.05em;
  color: #000;
  cursor: pointer;
  transition: all 0.3s ease;
}

.scope-filter-btn.active {
  border-color: var(--primary-color);
}

.search-active {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 2px 1rem;
  padding: 4px 10px 4px 12px;
  background: #ececec;
  border-radius: 999px;
  font-size: 0.78rem;
  color: #606060;
}

.search-active strong {
  color: #111827;
  font-weight: 600;
}

.link-btn {
  background: transparent;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 0.78rem;
}

.link-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.course-filter-wrapper {
  position: relative;
}

.course-filter-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  z-index: 1;
  pointer-events: none;
  font-size: 0.85rem;
}

.course-filter {
  min-width: 240px;
}

.course-filter >>> .vs__dropdown-toggle {
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  padding: 2px 8px 2px 32px;
  min-height: 36px;
  background: #fff;
}

.course-filter >>> .vs__dropdown-toggle:hover {
  border-color: #b0b0b0;
}

.course-filter >>> .vs__search,
.course-filter >>> .vs__search:focus {
  font-size: 0.85rem;
  margin: 0;
}

.course-filter >>> .vs__search::placeholder {
  color: #9ca3af;
}

.course-filter >>> .vs__selected {
  font-size: 0.85rem;
  color: #111827;
}

.course-filter >>> .vs__open-indicator {
  fill: #9ca3af;
}

.course-filter >>> .vs__dropdown-menu {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  padding: 6px;
  margin-top: 8px;
  max-height: 260px;
}

.course-filter >>> .vs__dropdown-option {
  padding: 8px 12px;
  border-radius: 6px;
  white-space: normal;
  line-height: 1.35;
  font-size: 0.85rem;
  color: #374151;
}

.course-filter >>> .vs__dropdown-option--highlight {
  background: #f3f4f6;
  color: #111827;
}

.course-filter >>> .vs__no-options {
  padding: 12px;
  color: #9ca3af;
  font-size: 0.85rem;
}

@media (max-width: 600px) {
  .announcements-inner {
    padding: 0.75rem 1rem 0;
  }

  .announcements-page-header {
    gap: 12px;
  }

  .announcements-page-header .page-label {
    font-size: 1.25rem;
  }

  .header-actions {
    background: transparent;
    padding: 0; 
    border-radius: 0;
    margin-bottom: 1.25rem;
    box-shadow: none;
  }

  .filter-row {
    justify-content: flex-start;
    gap: 0.5rem;
  }

  .filter-controls {
    display: contents;
  }

  .scope-filter-btn {
    padding: 0.7rem;
    letter-spacing: 0.02em;
  }

  .scope-filter-btn.flex {
    flex: 1 1 auto;
  }

  .course-filter-wrapper {
    order: 1;
    flex: 1 1 100%;
  }

  .course-filter {
    min-width: 0;
  }
}
</style>
